import firebase from "firebase/app";
import { shuffle, randomChoise } from "@/utils";
import { GAME_STATE, MASTER_ROLE } from "@/class/const";
import { GamePlayer, GamePlayerData } from "@/class/gamePlayer";
import { History } from "@/class/history";
import { Time } from "@/class/time";
import {
  Room,
  RoomSettings,
  Regulation,
  ReadyPlayer,
  ResultLog
} from "@/class/room";
import { RoleId, Side, GameState, CauseOfDeath } from "@/enum";

export interface GameLogOption {
  voteResult?: VoteResult;
  nightVoteResult?: NightVoteResult;
  openRole?: RoleId;
  nightAction?: NightAction;
  takeOtherAction?: TakeOtherAction;
  roleComingOut?: RoleComingOut;
  selfProclaimNotice?: RoleComingOut;
  policeResult?: PoliceResult;
  investigatorResult?: InvestigatorResult;
  gameResult?: GameResult;
}

export interface GameLog extends GameLogOption {
  day: number;
  state: GameState;
  timestamp: firebase.firestore.Timestamp;
}

export interface ReplayLog extends GameLog {
  action?: PlayLog;
}

export interface Vote {
  vote: { [playerId: string]: string[] };
  abandon: string[];
}

export interface VoteResult {
  votedData: Vote;
  finalVotePlayerIds?: string[];
  punishedPlayerId?: string;
  players: PlayerDataOf;
}

export interface NightVoteResult {
  nightVotedData: Vote;
  suspectedPlayerId?: string;
  players: PlayerDataOf;
}

export type PlayerDataOf = { [id: string]: GamePlayerData };

export interface NightAction {
  mafiaTargetId: string;
  playLogs: PlayLog[];
}

export interface PlayLog {
  actorIds: string[];
  targetIds?: string[];
  mafiaTargetId?: string;
  beforePlayers: PlayerDataOf;
  afterPlayers: PlayerDataOf;
}

export interface TakeOtherAction {
  players: PlayerDataOf;
}

export interface RoleComingOut {
  player: GamePlayerData;
  roleId: "" | RoleId;
}

export interface PoliceResult {
  player: PlayerDataOf;
  targetPlayer: PlayerDataOf;
  isMafia: boolean;
}

export interface InvestigatorResult {
  player: PlayerDataOf;
  targetPlayer: PlayerDataOf;
  hasWeapon: boolean;
}

export interface GameResult {
  players: PlayerDataOf;
}

export interface CutinLog {
  roleComingOut?: RoleComingOut;
  selfProclaimNotice?: RoleComingOut;
  policeResult?: PoliceResult;
  investigatorResult?: InvestigatorResult;
}

export interface GameData {
  masterId: string;
  readyPlayers: { [id: string]: ReadyPlayer };
  players: PlayerDataOf;
  state: GameState;
  dayCount: number;

  finalVote: {
    playerIds: string[];
    talkIndex: number;
  };

  settings: RoomSettings;

  endTime: number;
  stateLogs: GameLog[];
}

export class Game {
  private room: Room;
  public roomId = "";
  public id = "";
  public data: GameData;
  public gamePlayerOf: { [id: string]: GamePlayer } = {};
  private history: History | null = null;
  private nextState = GameState.ready;
  private prevState = GameState.ready;
  private isProsseccing = false;
  private time = new Time();

  constructor(room: Room) {
    this.room = room;
    this.roomId = room.id;
    this.data = this.createNewData(room);
  }

  private createNewData(room: Room) {
    return {
      masterId: room.masterId,
      settings: room.settings,
      readyPlayers: {},
      players: {},
      dayCount: 0,
      finalVote: {
        playerIds: [],
        talkIndex: 0
      },
      state: GameState.ready,
      endTime: 0,
      logs: [],
      stateLogs: []
    };
  }

  public async newGame(room: Room, players: { [id: string]: ReadyPlayer }) {
    this.data = this.createNewData(room);
    this.data.readyPlayers = players;
    await this.setRemainTime(this.timerSettings.ready);

    const docRef = await firebase
      .firestore()
      .collection(`room/${this.roomId}/game`)
      .add(this.data);
    const gameId = docRef.id;

    this.init(gameId);

    return gameId;
  }

  public init(gameId: string) {
    this.id = gameId;
    this.startObserveGame();

    this.history = new History(this.roomId, gameId);
  }

  private startObserveGame() {
    this.gameDocRef.onSnapshot(snapshot => {
      this.data = snapshot.data() as GameData;

      this.gamePlayerOf = {};
      Object.entries(this.data.players).map(([playerId, data]) => {
        this.gamePlayerOf[playerId] = new GamePlayer(
          this.roomId,
          this.id,
          data
        );
      });
    });
  }

  public unsubscribe() {
    this.gameDocRef.onSnapshot(() => null);
  }

  public get gameDocRef(): firebase.firestore.DocumentReference {
    return firebase.firestore().doc(`room/${this.roomId}/game/${this.id}`);
  }

  public async updateGame() {
    await this.gameDocRef.update(this.data);
  }

  /**
   * プレイヤー追加処理
   */
  private registerPlayers() {
    const playersDataOf: PlayerDataOf = {};

    const mustUseRoles = Array.from(this.settings.mustUseRoles);
    const randomUseRoles = Array.from(this.settings.randomUseRoles);

    // 優先役職選択者を先に処理する
    this.readyPlayers.forEach(player => {
      if (player.selectedRoleId) {
        playersDataOf[player.id] = this.initializePlayer(
          player,
          player.selectedRoleId
        );

        // 割り当てたロールを差し引く
        if (mustUseRoles.includes(player.selectedRoleId)) {
          const index = mustUseRoles.indexOf(player.selectedRoleId);
          mustUseRoles.splice(index, 1);
        } else {
          const index = randomUseRoles.indexOf(player.selectedRoleId);
          randomUseRoles.splice(index, 1);
        }
      }
    });

    shuffle(this.readyPlayers)
      .filter(player => !player.selectedRoleId)
      .forEach(player => {
        // 残りのロール
        const roleId =
          shuffle(mustUseRoles).shift() ||
          shuffle(randomUseRoles).shift() ||
          RoleId.people;

        playersDataOf[player.id] = this.initializePlayer(player, roleId);
      });

    this.data.players = playersDataOf;
    this.updateGame();
  }

  public initializePlayer(player: ReadyPlayer, roleId: RoleId): GamePlayerData {
    return {
      id: player.id,
      name: player.name,
      roleId: roleId,
      ready: true,
      restShield: roleId === RoleId.bomber ? 1 : 0,
      shield: roleId === RoleId.bomber ? 1 : 0,
      restBullet:
        roleId === RoleId.sniper ? this.settings.roleSettings.bullet : 0,
      bullet: roleId === RoleId.sniper ? this.settings.roleSettings.bullet : 0,
      restArmor:
        roleId === RoleId.survivor ? this.settings.roleSettings.armor : 0,
      armor: roleId === RoleId.survivor ? this.settings.roleSettings.armor : 0,
      needSteal:
        roleId === RoleId.pickpocket ? this.settings.roleSettings.needSteal : 0,
      selectedSelfAppointedRoleId: 0,
      selfAppointedRoleId: 0,
      isOpened: false,
      bomb: false,
      selectedPlayerId: "",
      lastNightSelectedPlayerId: "",
      votedPlayerId: "",
      status: {
        eatCrab: false,
        tempted: false,
        swindled: false,
        guard: false,
        stolen: false,
        success: null,
        killed: false
      },
      nightActionResult: [],
      stealCount: 0,
      useArmor: false,
      detonate: false,
      takeOther: false,
      isWinner: false,
      turnEnd: false
    };
  }

  public get readyPlayers() {
    return Object.values(this.data.readyPlayers).sort((a, b) =>
      a.id > b.id ? 1 : -1
    );
  }

  public get players(): GamePlayer[] {
    return Object.values(this.gamePlayerOf).sort((a, b) =>
      a.id > b.id ? 1 : -1
    );
  }

  public get masterId() {
    return this.data.masterId;
  }

  public getReadyPlayer(playerId: string): ReadyPlayer {
    return this.data.readyPlayers[playerId];
  }

  public getGamePlayer(playerId: string): GamePlayer {
    return this.gamePlayerOf[playerId];
  }

  public getPlayerRole(playerId: string) {
    return this.gamePlayerOf[playerId].role;
  }

  public get gameState(): GameState {
    return this.data.state;
  }

  public get isLoading(): boolean {
    return this.gameState === GameState.loading;
  }

  public get isReady(): boolean {
    return this.gameState === GameState.ready;
  }

  public get isFirstNight(): boolean {
    return this.gameState === GameState.firstNight;
  }

  public get isNight(): boolean {
    return this.gameState === GameState.night;
  }

  public get isDay(): boolean {
    return this.gameState === GameState.day;
  }

  public get isVoteStart(): boolean {
    return this.gameState === GameState.voteStart;
  }

  public get isFinalVoteReady(): boolean {
    return this.gameState === GameState.finalVoteReady;
  }

  public get isFinalVoteStart(): boolean {
    return this.gameState === GameState.finalVoteStart;
  }

  public get isTakeOther(): boolean {
    return this.gameState === GameState.takeOther;
  }

  public get isGameover(): boolean {
    return this.gameState === GameState.gameover;
  }

  public get settings() {
    return this.data.settings;
  }

  public get regulation(): Regulation {
    return {
      playersCount: this.players.length,
      settings: this.settings
    };
  }

  public get stateLogs(): GameLog[] {
    return this.data.stateLogs;
  }

  public get historyLogs(): GameLog[] {
    return this.history?.logs || [];
  }

  public get cutinLogs(): CutinLog[] {
    return this.history?.cutinLogs || [];
  }

  public get roomFinalVotePlayerIds(): string[] {
    return this.data.finalVote.playerIds;
  }

  public get stateLabel(): string {
    return GAME_STATE[this.data.state];
  }

  public get limitDate() {
    return new Date(this.data.endTime);
  }

  public get endTime() {
    return this.data.endTime;
  }

  public get timerSettings() {
    return this.settings.timerSettings;
  }

  public get isFullclose() {
    return this.settings.isFullclose;
  }

  public set isFullclose(val) {
    this.settings.isFullclose = val;
  }

  public get dayVote() {
    return this.settings.dayVote;
  }

  public get canNightCommunication() {
    return this.settings.canNightCommunication;
  }

  public set canNightCommunication(val) {
    this.settings.canNightCommunication = val;
  }

  public get canGallaryFullopen() {
    return this.settings.canGallaryFullopen;
  }

  public set canGallaryFullopen(val) {
    this.settings.canGallaryFullopen = val;
  }

  public get finalVoteTalkerId(): string {
    return this.data.finalVote.playerIds[this.data.finalVote.talkIndex];
  }

  public get roleOptions() {
    return {
      isFirstNightRandomWhite: this.settings.isFirstNightRandomWhite
    };
  }

  public get useRoles() {
    const useRoleIds = this.settings.mustUseRoles.concat(
      this.settings.randomUseRoles
    );
    return useRoleIds
      .filter((x, i, self) => self.indexOf(x) === i)
      .map(roleId => MASTER_ROLE[roleId])
      .sort((a, b) => a.code - b.code);
  }

  public async setNextState() {
    if (this.isProsseccing) {
      return;
    }
    this.isProsseccing = true;

    this.changeMafiaFromGodFather();

    this.data.stateLogs = [];
    switch (this.gameState) {
      case GameState.ready: {
        this.nextState = GameState.firstNight;
        this.registerPlayers();
        break;
      }
      case GameState.firstNight:
      case GameState.night: {
        this.nextState = GameState.day;

        this.resetNightActionResult();
        this.allPlayerStatusReset();

        if (this.settings.isFirstNightRandomWhite) {
          this.policeRandomWhiteSelect();
        }

        this.nightVote();

        const nightAction = this.nightAction(this.gameState, this.data.players);

        this.pushStateLog({ nightAction });

        break;
      }
      case GameState.day: {
        if (this.settings.dayVote) {
          this.voteEnd();
        } else {
          this.nextState = GameState.voteStart;
        }
        break;
      }
      case GameState.voteStart:
      case GameState.finalVoteStart: {
        this.voteEnd();
        break;
      }
      case GameState.finalVoteReady: {
        if (
          this.data.finalVote.talkIndex <
          this.data.finalVote.playerIds.length - 1
        ) {
          this.data.finalVote.talkIndex++;
        } else {
          this.nextState = GameState.finalVoteStart;
        }
        break;
      }
      case GameState.voteEnd: {
        if (this.data.finalVote.playerIds.length) {
          this.nextState = GameState.finalVoteReady;
        } else {
          this.nextState = GameState.night;
        }
        break;
      }
      case GameState.takeOther: {
        if (
          this.prevState === GameState.voteStart ||
          this.prevState === GameState.finalVoteStart ||
          this.prevState === GameState.day
        ) {
          this.nextState = GameState.night;
        } else if (this.prevState === GameState.night) {
          this.nextState = GameState.day;
        }

        const takeOtherAction = {
          players: JSON.parse(JSON.stringify(this.data.players))
        };
        this.pushStateLog({ takeOtherAction });

        this.takeOther();
        break;
      }
    }
    this.prevState = this.data.state;

    const result = this.calcResult(this.gamePlayerOf);

    if (result.winnerPlayerIds.length) {
      this.nextState = GameState.gameover;

      const gameResult = {
        players: JSON.parse(JSON.stringify(this.data.players))
      };

      this.pushStateLog({ gameResult });
      this.room.pushResultLog(result);
    }

    this.data.state = this.nextState;
    if (this.nextState === GameState.day) {
      this.data.dayCount++;
    }

    this.resetPlayersAction();

    let remainTime = this.settings.timerSettings[this.data.state] || 0;

    if (this.nextState === GameState.day) {
      const liver = this.players.filter(player =>
        this.roleAndLiveFilter(player, [])
      );
      remainTime = this.settings.timerSettings.dayByLiver * liver.length;
      if (remainTime > this.settings.timerSettings.dayMax) {
        remainTime = this.settings.timerSettings.dayMax;
      }
    }

    await this.setRemainTime(remainTime);
    await this.updateGame();

    this.isProsseccing = false;
  }

  public setRole(playerId: string, roleId: RoleId): void {
    this.data.readyPlayers[playerId].selectedRoleId = roleId;
    const key = `readyPlayers.${playerId}.selectedRoleId`;
    this.gameDocRef.update({
      [key]: roleId
    });
  }

  private resetPlayersAction() {
    this.players.forEach(p => {
      p.resetVotedPlayerId();
      p.resetSelectedPlayerId();
      p.resetTurnEnd();
      p.detonate = false;
      p.useArmor = false;
    });
  }

  public makeVoteData(): Vote {
    const data: Vote = {
      vote: {},
      abandon: []
    };
    this.players
      .filter(player => !player.isDead)
      .forEach(player => {
        if (player.votedPlayerId) {
          if (data.vote[player.votedPlayerId]) {
            data.vote[player.votedPlayerId].push(player.id);
          } else {
            data.vote[player.votedPlayerId] = [player.id];
          }
        } else {
          data.abandon.push(player.id);
        }
      });

    return data;
  }

  private getMostVotedPlayerIds(voteData: Vote) {
    const mostVotedCount = Math.max(
      ...Object.values(voteData.vote).map(playerIds => playerIds.length)
    );

    return Object.keys(voteData.vote).filter(
      playerId => voteData.vote[playerId].length === mostVotedCount
    );
  }

  private voteEnd() {
    this.nextState = GameState.voteEnd;
    this.data.finalVote.talkIndex = 0;
    this.data.finalVote.playerIds = [];

    const votedData = this.makeVoteData();
    const voteResult = this.isFinalVoteStart
      ? this.culcFinalVoteResult(votedData)
      : this.culcVoteResult(votedData);

    if (voteResult.punishedPlayerId) {
      this.votedKill(voteResult.punishedPlayerId);
    }

    if (voteResult.finalVotePlayerIds) {
      this.data.finalVote.playerIds = voteResult.finalVotePlayerIds;
    }

    this.pushStateLog({ voteResult });
  }

  private culcNightVoteResult(nightVoteData: Vote) {
    const mostVotedPlayerIds = this.getMostVotedPlayerIds(nightVoteData);
    return randomChoise(mostVotedPlayerIds);
  }

  private culcVoteResult(votedData: Vote): VoteResult {
    const voteResult: VoteResult = {
      votedData,
      players: JSON.parse(JSON.stringify(this.data.players))
    };

    const mostVotedCount = Math.max(
      ...Object.values(votedData.vote).map(vote => vote.length)
    );
    const mostVotedPlayerIds = this.getMostVotedPlayerIds(votedData);

    /**
     * 最大得票数が1票の場合は処刑なし
     */
    if (mostVotedCount === 1) {
      return voteResult;
    }

    if (mostVotedPlayerIds.length > 1) {
      if (this.settings.enableFinalVote) {
        voteResult.finalVotePlayerIds = mostVotedPlayerIds;
      } else if (this.settings.enableRundomExecution) {
        voteResult.punishedPlayerId = randomChoise(mostVotedPlayerIds);
      }
    }

    if (mostVotedPlayerIds.length === 1) {
      voteResult.punishedPlayerId = randomChoise(mostVotedPlayerIds);
    }

    return voteResult;
  }

  private culcFinalVoteResult(votedData: Vote): VoteResult {
    const voteResult: VoteResult = {
      votedData,
      players: JSON.parse(JSON.stringify(this.data.players))
    };

    const mostVotedCount = Math.max(
      ...Object.values(votedData.vote).map(vote => vote.length)
    );
    const mostVotedPlayerIds = this.getMostVotedPlayerIds(votedData);

    /**
     * 最大得票数が1票の場合は処刑なし
     */
    if (mostVotedCount === 1) {
      return voteResult;
    }

    if (!this.settings.enableRundomExecution && mostVotedPlayerIds.length > 1) {
      return voteResult;
    }

    voteResult.punishedPlayerId = randomChoise(mostVotedPlayerIds);

    return voteResult;
  }

  private votedKill(punishedPlayerId: string) {
    const punishedPlayer = this.gamePlayerOf[punishedPlayerId];
    if (
      !this.isFullclose ||
      punishedPlayer.roleId === RoleId.nobleman ||
      punishedPlayer.roleId === RoleId.daughter
    ) {
      punishedPlayer.open();
    }

    if (punishedPlayer.roleId === RoleId.nobleman) {
      return;
    }

    this.playerDeath(punishedPlayer, CauseOfDeath.punished);

    if (punishedPlayer.roleId === RoleId.daughter) {
      this.players
        .filter(player => player.votedPlayerId === punishedPlayerId)
        .forEach(player =>
          player.death(this.data.dayCount, CauseOfDeath.daughter)
        );
    }
  }

  public async setRemainTime(remainTime: number) {
    const gap = await this.time.getGap();
    this.data.endTime = Date.now() + remainTime * 1000 + gap;
  }

  private policeRandomWhiteSelect() {
    this.players
      .filter(player => this.roleAndLiveFilter(player, [RoleId.police]))
      .forEach(player => {
        const notMafiaPlayer = this.players.filter(p => {
          return player.id !== p.id && !p.role.mafia;
        });
        player.selectedPlayerId = randomChoise(notMafiaPlayer).id;
      });
  }

  public get roleActionOrder() {
    return [
      { roleId: RoleId.dancer, action: this.tempt },
      { roleId: RoleId.prostitute, action: this.tempt },
      { roleId: RoleId.trickster, action: this.swindle },
      { roleId: RoleId.pickpocket, action: this.steal },
      { roleId: RoleId.doctor, action: this.doctor },
      { roleId: RoleId.survivor, action: this.armor },
      { roleId: RoleId.police, action: this.police },
      { roleId: RoleId.investigator, action: this.investigator },
      { roleId: RoleId.bomber, action: this.bomber },
      { roleId: RoleId.sniper, action: this.snipe },
      { roleId: RoleId.mafia, action: this.mafia },
      { roleId: RoleId.godfatherAndMafia, action: this.godFather },
      { roleId: RoleId.killer, action: this.killer }
    ];
  }

  public nightAction(
    state: GameState.firstNight | GameState.night,
    playerDataOf: PlayerDataOf
  ) {
    const playLogs: PlayLog[] = [];
    let mafiaCount = 0;
    let mafiaTargetId = "";

    const gamePlayerOf: { [key: string]: GamePlayer } = {};
    const gamePlayers = Object.values(playerDataOf).map(data => {
      const gamePlayer = new GamePlayer(this.roomId, this.id, data);
      gamePlayerOf[gamePlayer.id] = gamePlayer;
      return gamePlayer;
    });

    this.roleActionOrder
      .filter(roleAction => MASTER_ROLE[roleAction.roleId].activeIn[state])
      .forEach(roleAction => {
        gamePlayers
          .filter(player => this.roleAndLiveFilter(player, [roleAction.roleId]))
          .forEach(player => {
            const beforePlayers = JSON.parse(JSON.stringify(playerDataOf));

            if (player.roleId === RoleId.mafia && player.canNightAction) {
              mafiaCount++;
            }

            const actorIds = [player.id];
            const actors = this.players.filter(p => actorIds.includes(p.id));
            const targetPlayerId = player.selectedPlayerId;
            const targetPlayer = gamePlayerOf[targetPlayerId];

            // ここはなんかもっと綺麗にしたい
            const targetIds = this.getTargetIds(player, gamePlayers);

            if (!actors.some(p => p.canNightAction)) {
              playLogs.push({
                actorIds,
                targetIds,
                beforePlayers,
                afterPlayers: JSON.parse(JSON.stringify(playerDataOf))
              });

              return;
            }

            if (
              !(
                player.roleId === RoleId.mafia ||
                player.roleId === RoleId.godfatherAndMafia
              ) &&
              !this.existsCrabShield &&
              targetPlayer?.roleId === RoleId.crab
            ) {
              actors.forEach(p => this.eatCrab(p));
            }

            if (
              player.roleId === RoleId.dancer ||
              player.roleId === RoleId.prostitute
            ) {
              this.tempt(player, targetPlayer);
            } else if (player.roleId === RoleId.trickster) {
              this.swindle(targetPlayer);
            } else if (player.roleId === RoleId.pickpocket) {
              this.steal(player, targetPlayer);
            } else if (player.roleId === RoleId.doctor) {
              this.doctor(player, targetPlayer);
            } else if (player.roleId === RoleId.survivor) {
              this.armor(player);
            } else if (player.roleId === RoleId.police) {
              this.police(player, targetPlayer);
            } else if (player.roleId === RoleId.investigator) {
              this.investigator(player, targetPlayer);
            } else if (player.roleId === RoleId.bomber) {
              this.bomber(player, targetPlayer, gamePlayers);
            } else if (player.roleId === RoleId.sniper) {
              this.snipe(player, targetPlayer);
            } else if (player.roleId === RoleId.godfatherAndMafia) {
              this.godFather(player, targetPlayer);
            } else if (player.roleId === RoleId.killer) {
              this.killer(player, targetPlayer);
            }

            // roleAction.action(player, targetPlayer)

            playLogs.push({
              actorIds,
              targetIds,
              beforePlayers,
              afterPlayers: JSON.parse(JSON.stringify(playerDataOf))
            });

            // マフィアは全員で1アクションなので最後のマフィア以外は処理が発生しない
            if (
              player.roleId === RoleId.mafia &&
              this.isLastActiveMafia(mafiaCount)
            ) {
              const actorIds = this.activeMafia.map(p => p.id);
              mafiaTargetId = this.choseMafiaTarget();
              const targetPlayer = gamePlayerOf[mafiaTargetId];

              if (
                !this.existsCrabShield &&
                targetPlayer?.roleId === RoleId.crab
              ) {
                actors.forEach(p => this.eatCrab(p));
              }

              this.mafia(targetPlayer);

              playLogs.push({
                actorIds,
                mafiaTargetId,
                beforePlayers,
                afterPlayers: JSON.parse(JSON.stringify(playerDataOf))
              });
            }
          });
      });

    const nightAction = {
      mafiaTargetId,
      playLogs
    };

    return nightAction;
  }

  getTargetIds(player: GamePlayer, gamePlayers: GamePlayer[]) {
    // 爆弾魔で起爆の場合は爆弾設置済みの人が対象となる
    if (player.detonate) {
      return gamePlayers.filter(p => p.bomb).map(p => p.id);
    }

    return player.selectedPlayerId ? [player.selectedPlayerId] : [];
  }

  isLastActiveMafia(mafiaCount: number) {
    return mafiaCount === this.activeMafia.length;
  }

  get activeMafia() {
    return this.players.filter(
      player => player.roleId === RoleId.mafia && player.canNightAction
    );
  }

  private get existsCrabShield() {
    return (
      this.settings.existsCrabShield && this.gameState === GameState.firstNight
    );
  }

  private nightVote() {
    const nightVotedData = this.makeNightVoteData();
    const nightVoteResult: NightVoteResult = {
      nightVotedData,
      players: JSON.parse(JSON.stringify(this.data.players))
    };

    const suspectedPlayerId = this.culcNightVoteResult(nightVotedData);
    if (suspectedPlayerId) {
      nightVoteResult.suspectedPlayerId = suspectedPlayerId;
    }

    this.pushStateLog({ nightVoteResult });
  }

  public makeNightVoteData(): Vote {
    const data: Vote = {
      vote: {},
      abandon: []
    };
    this.players
      .filter(player => {
        if (this.gameState === GameState.firstNight) {
          return !player.role.activeIn[GameState.firstNight];
        } else if (this.gameState === GameState.night) {
          return !player.role.activeIn[GameState.night];
        }
      })
      .filter(player => !player.isDead)
      .forEach(player => {
        if (player.votedPlayerId) {
          if (data.vote[player.votedPlayerId]) {
            data.vote[player.votedPlayerId].push(player.id);
          } else {
            data.vote[player.votedPlayerId] = [player.id];
          }
        } else {
          data.abandon.push(player.id);
        }
      });

    return data;
  }

  private choseMafiaTarget() {
    const targetPlayerIds: string[] = [];
    this.activeMafia.forEach(player => {
      if (this.isFirstNight && !player.role.activeIn[GameState.firstNight]) {
        return;
      }

      if (!player.selectedPlayerId) {
        return;
      }

      targetPlayerIds.push(player.selectedPlayerId);
    });

    if (targetPlayerIds.length === 0) {
      return "";
    }

    const mafiaTargetId = randomChoise(targetPlayerIds);

    return mafiaTargetId;
  }

  private eatCrab(player: GamePlayer): boolean {
    if (this.existsCrabShield) {
      return false;
    }

    const targetPlayer = player.selectedPlayerId
      ? this.gamePlayerOf[player.selectedPlayerId]
      : null;
    if (!targetPlayer || targetPlayer.roleId !== RoleId.crab) {
      return false;
    }

    player.eatCrab();

    if (!targetPlayer.isDead) {
      targetPlayer.death(this.data.dayCount, CauseOfDeath.eaten);
    }

    return true;
  }

  private tempt(player: GamePlayer, targetPlayer: GamePlayer) {
    player.lastNightSelectedPlayerId = player.selectedPlayerId;

    if (!targetPlayer) {
      return;
    }

    if (
      targetPlayer.roleId === RoleId.dancer ||
      targetPlayer.roleId === RoleId.prostitute
    ) {
      return;
    }

    if (!targetPlayer.role.activeIn[GameState.night]) {
      return;
    }

    targetPlayer.tempted();
  }

  private swindle(targetPlayer: GamePlayer) {
    if (!targetPlayer) {
      return;
    }

    targetPlayer.swindled();
  }

  private steal(player: GamePlayer, targetPlayer: GamePlayer) {
    if (!targetPlayer) {
      return;
    }

    // スナイパーのみ特殊判定
    if (targetPlayer.roleId === RoleId.sniper && targetPlayer.restBullet <= 0) {
      return;
    }

    if (!targetPlayer.role.weapon && !targetPlayer.status.swindled) {
      return;
    }

    targetPlayer.stolen();
    player.stole();
  }

  private doctor(player: GamePlayer, targetPlayer: GamePlayer) {
    player.lastNightSelectedPlayerId = player.selectedPlayerId;

    if (!targetPlayer) {
      return;
    }

    targetPlayer.guard();
  }

  private armor(player: GamePlayer) {
    if (!player.useArmor) {
      return;
    }

    if (!player.restArmor) {
      return;
    }

    player.restArmor--;
    player.guard();
  }

  private police(player: GamePlayer, targetPlayer: GamePlayer) {
    if (!targetPlayer) {
      return;
    }

    let mafia = targetPlayer.role.mafia;
    if (targetPlayer.status.swindled) {
      mafia = true;
    }

    if (mafia) {
      player.status.success = true;
    } else {
      player.status.success = false;
    }
  }

  private investigator(player: GamePlayer, targetPlayer: GamePlayer) {
    if (!targetPlayer) {
      return;
    }

    let weapon = targetPlayer.role.weapon;
    // スナイパーが弾切れしていた場合
    if (
      targetPlayer.roleId === RoleId.sniper &&
      targetPlayer.restBullet === 0
    ) {
      weapon = false;
    }

    // ペテンにかけられている場合
    if (targetPlayer.status.swindled) {
      weapon = true;
    }

    // 武器を盗まれていた場合
    if (targetPlayer.status.stolen) {
      weapon = false;
    }

    // 武器を盗んでいた場合
    if (
      targetPlayer.roleId === RoleId.pickpocket &&
      targetPlayer.status.success
    ) {
      weapon = true;
    }

    if (weapon) {
      player.status.success = true;
    } else {
      player.status.success = false;
    }
  }

  private bomber(
    player: GamePlayer,
    targetPlayer: GamePlayer,
    gamePlayers: GamePlayer[]
  ) {
    if (player.detonate) {
      gamePlayers
        .filter(p => !p.isDead)
        .filter(p => p.bomb)
        .forEach(p => {
          const data: GameLogOption = {};
          if (p.roleId === RoleId.assassin || p.roleId === RoleId.boy) {
            data.openRole = p.roleId;
          }
          this.playerDeath(p, CauseOfDeath.bomber);
          p.bomb = false;
        });
    } else if (targetPlayer) {
      targetPlayer.bomb = true;
    }
  }

  private snipe(player: GamePlayer, targetPlayer: GamePlayer) {
    if (!targetPlayer) {
      return;
    }

    player.restBullet--;

    player.status.success = this.nightKill(targetPlayer, player.role.code);
  }

  private mafia(targetPlayer: GamePlayer) {
    if (!targetPlayer) {
      return;
    }

    this.nightKill(targetPlayer, RoleId.mafia);
  }

  private godFather(player: GamePlayer, targetPlayer: GamePlayer) {
    if (!targetPlayer) {
      return;
    }

    player.status.success = this.nightKill(targetPlayer, player.role.code);
  }

  private killer(player: GamePlayer, targetPlayer: GamePlayer) {
    if (!targetPlayer) {
      return;
    }

    player.status.success = this.nightKill(targetPlayer, player.role.code);
  }

  private nightKill(targetPlayer: GamePlayer, roleId: RoleId) {
    if (!this.canNightKill(targetPlayer)) {
      return false;
    }

    if (!targetPlayer.isDead) {
      const data: GameLogOption = {};
      if (
        targetPlayer.roleId === RoleId.assassin ||
        targetPlayer.roleId === RoleId.boy
      ) {
        data.openRole = targetPlayer.roleId;
      }

      if (roleId === RoleId.mafia || roleId === RoleId.godfatherAndMafia) {
        this.playerDeath(targetPlayer, CauseOfDeath.mafia);
      }

      if (roleId === RoleId.killer) {
        this.playerDeath(targetPlayer, CauseOfDeath.killer);
      }

      if (roleId === RoleId.sniper) {
        this.playerDeath(targetPlayer, CauseOfDeath.sniper);
      }
    }

    return true;
  }

  private roleAndLiveFilter(
    player: GamePlayer,
    roleIds: RoleId[] | []
  ): boolean {
    if (player.isDead) {
      return false;
    }
    if (roleIds.length === 0) {
      return true;
    }
    for (const roleId of roleIds) {
      if (player.roleId === roleId) {
        return true;
      }
    }
    return false;
  }

  private takeOther() {
    this.players
      .filter(p => p.takeOther)
      .forEach(player => {
        player.takeOther = false;

        if (player.selectedPlayerId) {
          const targetPlayer = this.gamePlayerOf[player.selectedPlayerId];
          // TODO このログもプロパティから出力するようにする
          targetPlayer.setNightActionResult(`あなたは殺害されました`);

          if (player.roleId === RoleId.boy) {
            targetPlayer.death(this.data.dayCount, CauseOfDeath.boy);
          }

          if (player.roleId === RoleId.assassin) {
            targetPlayer.death(this.data.dayCount, CauseOfDeath.assassin);
          }
        }
      });
  }

  private canNightKill(targetPlayer: GamePlayer): boolean {
    let result = true;
    if (targetPlayer.status.guard) {
      result = false;
    }
    if (targetPlayer.roleId === RoleId.killer) {
      result = false;
    }
    if (targetPlayer.restShield) {
      targetPlayer.restShield--;
      targetPlayer.guard();
      result = false;
    }
    if (targetPlayer.roleId === RoleId.godfather) {
      result = false;
    }
    return result;
  }

  private allPlayerStatusReset() {
    for (const player of this.players) {
      player.resetStatus();
    }
  }

  private resetNightActionResult() {
    for (const player of this.players) {
      player.setNightActionResult("");
    }
  }

  private changeMafiaFromGodFather() {
    const isDeadAllMafia =
      this.players.filter(p => {
        return p.roleId === RoleId.mafia && !p.isDead;
      }).length === 0;

    if (!isDeadAllMafia) return;

    for (const player of this.players.filter(
      p => p.roleId === RoleId.godfather
    )) {
      player.roleId = RoleId.godfatherAndMafia;
    }
  }

  public calcResult(playerDataOf: PlayerDataOf): ResultLog {
    const gamePlayers = Object.values(playerDataOf).map(
      data => new GamePlayer(this.roomId, this.id, data)
    );
    const result: ResultLog = {
      gameId: this.id,
      winnerPlayerIds: [],
      winnerRoleIds: [],
      playerRoles: {}
    };

    // 陣営別に生きてるプレイヤーを分ける
    const eachSideCount: { [key: string]: GamePlayer[] } = {
      [Side.people]: [],
      [Side.mafia]: [],
      [Side.mafiaLeader]: [],
      [Side.neutrality]: [],
      [Side.outlaw]: []
    };

    gamePlayers.forEach(player => eachSideCount[player.role.side].push(player));

    let winners: GamePlayer[] = [];
    if (this.peopleWin(eachSideCount)) {
      winners = eachSideCount[Side.people];
      result.winnerRoleIds = [RoleId.people];
    } else if (this.mafiaWin(eachSideCount)) {
      winners = winners.concat(eachSideCount[Side.mafiaLeader]);
      winners = winners.concat(eachSideCount[Side.mafia]);
      result.winnerRoleIds = [RoleId.mafia];
    } else if (this.outlawWin(eachSideCount)) {
      result.winnerRoleIds = [];
      const killers = gamePlayers.filter(player => {
        return player.roleId === RoleId.killer;
      });
      const bombers = gamePlayers.filter(player => {
        return player.roleId === RoleId.bomber;
      });

      if (killers.length) {
        result.winnerRoleIds.push(RoleId.killer);
      }

      if (bombers.length) {
        result.winnerRoleIds.push(RoleId.bomber);
      }

      winners = gamePlayers.filter(player => {
        return (
          player.roleId === RoleId.bomber || player.roleId === RoleId.killer
        );
      });
    }

    // 規定回数盗んだスリがいたら勝ち
    if (this.pushPickpocketWin(winners)) {
      result.winnerRoleIds = [RoleId.pickpocket];
    }

    if (winners.length > 0) {
      // 新人女優が処刑された場合は新人女優の勝ち
      const cinderellaGirlWinners = this.cinderellaGirlWin();
      if (cinderellaGirlWinners.length > 0) {
        winners.push(...cinderellaGirlWinners);
        result.winnerRoleIds.push(RoleId.cinderellaGirl);
      }

      // サバイバーが生存していた場合サバイバーの勝ち
      const survivorWinners = this.survivorWin();
      if (survivorWinners.length > 0) {
        winners.push(...survivorWinners);
        result.winnerRoleIds.push(RoleId.survivor);
      }

      // TODO このフラグはいらなくなるはず
      winners.forEach(player => {
        player.isWinner = true;
      });
      result.winnerPlayerIds = winners.map(player => player.id);
      gamePlayers.forEach(player => {
        result.playerRoles[player.id] = player.role.code;
      });

      this.nextState = GameState.gameover;
    }

    return result;
  }

  private peopleWin(eachSideCount: { [key: string]: GamePlayer[] }): boolean {
    let result = false;
    if (
      eachSideCount[Side.mafiaLeader].filter(p => !p.isDead).length === 0 &&
      eachSideCount[Side.outlaw].filter(p => !p.isDead).length === 0
    ) {
      result = true;
    }
    return result;
  }

  private mafiaWin(eachSideCount: { [key: string]: GamePlayer[] }): boolean {
    let result = false;
    const people =
      eachSideCount[Side.people].filter(p => !p.isDead).length +
      eachSideCount[Side.mafia].filter(p => !p.isDead).length +
      eachSideCount[Side.neutrality].filter(p => !p.isDead).length;

    if (
      eachSideCount[Side.outlaw].filter(p => !p.isDead).length === 0 &&
      eachSideCount[Side.mafiaLeader].filter(p => !p.isDead).length >= people
    ) {
      result = true;
    }
    return result;
  }

  private outlawWin(eachSideCount: { [key: string]: GamePlayer[] }): boolean {
    let result = false;
    if (
      eachSideCount[Side.mafia].filter(p => !p.isDead).length === 0 &&
      eachSideCount[Side.mafiaLeader].filter(p => !p.isDead).length === 0 &&
      eachSideCount[Side.people].filter(p => !p.isDead).length === 0 &&
      eachSideCount[Side.neutrality].filter(p => !p.isDead).length === 0
    ) {
      result = true;
    }
    return result;
  }

  private pushPickpocketWin(winner: GamePlayer[]) {
    let result = false;
    for (const player of this.players.filter(p => !p.isDead)) {
      if (player.roleId === RoleId.pickpocket) {
        if (player.stealCount >= player.needSteal) {
          winner.push(player);
          result = true;
        }
      }
    }
    return result;
  }

  private survivorWin(): GamePlayer[] {
    return this.players.filter(p =>
      this.roleAndLiveFilter(p, [RoleId.survivor])
    );
  }

  private cinderellaGirlWin(): GamePlayer[] {
    return this.players
      .filter(player => player.roleId === RoleId.cinderellaGirl)
      .filter(p => p.isDead);
  }

  private playerDeath(player: GamePlayer, causeOfDeath: CauseOfDeath) {
    if (player.roleId === RoleId.assassin || player.roleId === RoleId.boy) {
      player.takeOther = true;
      player.open();
      this.nextState = GameState.takeOther;
    }
    player.death(this.data.dayCount, causeOfDeath);
  }

  public get activePlayers(): GamePlayer[] {
    if (
      this.gameState === GameState.day ||
      this.gameState === GameState.voteStart ||
      this.gameState === GameState.finalVoteStart
    ) {
      return this.players.filter(player => !player.isDead);
    } else if (this.gameState === GameState.finalVoteReady) {
      return this.players.filter(
        player => player.id === this.finalVoteTalkerId
      );
    } else if (this.gameState === GameState.night) {
      return this.players.filter(
        player => !player.isDead && player.role.activeIn[GameState.night]
      );
    } else if (this.gameState === GameState.takeOther) {
      return this.players.filter(player => player.takeOther);
    }
    return [];
  }

  public get isAllPlayerTurnEnd(): boolean {
    const activePlayers =
      this.activePlayers.length === 0 ? this.players : this.activePlayers;

    return !activePlayers.some(player => !player.turnEnd);
  }

  public proclaimRole(playerId: string, roleId: RoleId) {
    const beforePlayer = JSON.parse(
      JSON.stringify(this.data.players[playerId])
    );

    const player = this.gamePlayerOf[playerId];
    player.selectedSelfAppointedRoleId = roleId;
    player.comingOut();

    const gameLog = this.makeLog({
      roleComingOut: {
        player: beforePlayer,
        roleId
      }
    });

    this.history?.addBackLog(gameLog);
  }

  public proclaimRoleNotice(playerId: string, roleId: RoleId) {
    const player = JSON.parse(JSON.stringify(this.data.players[playerId]));

    const gameLog = this.makeLog({
      selfProclaimNotice: {
        player,
        roleId
      }
    });

    this.history?.addBackLog(gameLog);
  }

  public proclaimPoliceResult(
    playerId: string,
    targetPlayerId: string,
    isMafia: boolean
  ) {
    const player = JSON.parse(JSON.stringify(this.data.players[playerId]));
    const targetPlayer = JSON.parse(
      JSON.stringify(this.data.players[targetPlayerId])
    );

    const gameLog = this.makeLog({
      policeResult: {
        player,
        targetPlayer,
        isMafia
      }
    });

    this.history?.addBackLog(gameLog);
  }

  public proclaimInvestigatorResult(
    playerId: string,
    targetPlayerId: string,
    hasWeapon: boolean
  ) {
    const player = JSON.parse(JSON.stringify(this.data.players[playerId]));
    const targetPlayer = JSON.parse(
      JSON.stringify(this.data.players[targetPlayerId])
    );

    const gameLog = this.makeLog({
      investigatorResult: {
        player,
        targetPlayer,
        hasWeapon
      }
    });

    this.history?.addBackLog(gameLog);
  }

  private pushStateLog(logData: GameLogOption): void {
    const gameLog = this.makeLog(logData);
    this.data.stateLogs.push(gameLog);
    this.history?.addBackLog(gameLog);
  }

  private makeLog(logData: GameLogOption) {
    const log: GameLog = {
      day: this.data.dayCount,
      state: this.data.state,
      timestamp: firebase.firestore.Timestamp.now(),
      ...logData
    };
    return log;
  }

  public resetGames() {
    this.room.resetGame();
  }
}
